// components/MessageBubble.tsx
import type { Message } from "../types";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Bot,
  Check,
  Clock,
  User,
  Volume2,
  Pause,
} from "lucide-react";
import React from "react";
import { Avatar, AvatarFallback, Tooltip } from "./ui";

interface MessageBubbleProps {
  message: Message;
  index: number;
  onRetry?: () => void;
  onPlayAudio?: () => void;
  onStopAudio?: () => void;
  isPlaying?: boolean;
  canPlayAudio?: boolean;
}

const StatusIcon = ({ status }: { status: Message["status"] }) => {
  switch (status) {
    case "sending":
      return (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-3 h-3"
        >
          <Clock className="w-3 h-3 text-slate-400" />
        </motion.div>
      );
    case "sent":
      return <Check className="w-3 h-3 text-green-400" />;
    case "error":
      return <AlertCircle className="w-3 h-3 text-red-400" />;
    default:
      return null;
  }
};

// Function to format AI response text with proper markdown-like formatting
const formatAIResponse = (text: string) => {
  // Split text into paragraphs
  const paragraphs = text.split("\n\n").filter((p) => p.trim());

  return paragraphs.map((paragraph, index) => {
    // Handle different types of formatting
    let formattedParagraph = paragraph.trim();

    // Handle bold text (**text** or ***text***)
    formattedParagraph = formattedParagraph.replace(
      /\*\*\*([^*]+)\*\*\*/g,
      '<strong class="font-bold text-blue-300">$1</strong>'
    );
    formattedParagraph = formattedParagraph.replace(
      /\*\*([^*]+)\*\*/g,
      '<strong class="font-semibold text-white">$1</strong>'
    );

    // Handle italic text (*text*)
    formattedParagraph = formattedParagraph.replace(
      /\*([^*]+)\*/g,
      '<em class="italic text-slate-200">$1</em>'
    );

    // Check if it's a header (starts with ***)
    const isMainHeader = paragraph.startsWith("***");
    const isSubHeader =
      paragraph.startsWith("**") && !paragraph.startsWith("***");
    const isBulletPoint =
      paragraph.trim().startsWith("*") && !paragraph.startsWith("**");

    if (isMainHeader) {
      return (
        <div key={index} className="mb-4">
          <h3
            className="text-lg font-bold text-blue-300 mb-2 border-b border-blue-500/30 pb-1"
            dangerouslySetInnerHTML={{ __html: formattedParagraph }}
          />
        </div>
      );
    } else if (isSubHeader) {
      return (
        <div key={index} className="mb-3">
          <h4
            className="text-base font-semibold text-purple-300 mb-2"
            dangerouslySetInnerHTML={{ __html: formattedParagraph }}
          />
        </div>
      );
    } else if (isBulletPoint) {
      return (
        <div key={index} className="mb-2 ml-4">
          <p
            className="text-sm text-slate-200 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: formattedParagraph.replace(/^\*/, "•"),
            }}
          />
        </div>
      );
    } else {
      return (
        <div key={index} className="mb-3">
          <p
            className="text-sm text-slate-200 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: formattedParagraph }}
          />
        </div>
      );
    }
  });
};

const messageVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.95,
    filter: "blur(4px)",
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
  },
  exit: {
    opacity: 0,
    y: -20,
    scale: 0.95,
    filter: "blur(4px)",
  },
};

const bubbleVariants = {
  initial: { scale: 0.95 },
  hover: {
    scale: 1.02,
    transition: { duration: 0.2 },
  },
  tap: { scale: 0.98 },
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  index,
  onRetry,
  onPlayAudio,
  onStopAudio,
  isPlaying = false,
  canPlayAudio = false,
}) => {
  const isUser = message.isUser;
  const isError = message.status === "error";

  const handleClick = () => {
    if (isError && onRetry) {
      onRetry();
    }
  };

  const handleAudioClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent bubble click when clicking audio button
    if (isPlaying && onStopAudio) {
      onStopAudio();
    } else if (!isPlaying && onPlayAudio) {
      onPlayAudio();
    }
  };

  return (
    <motion.div
      variants={messageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ delay: index * 0.1, duration: 0.4 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-6 group`}
    >
      {/* Avatar for AI messages */}
      {!isUser && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: index * 0.1 + 0.2 }}
          className="mr-3 mt-1 flex-shrink-0"
        >
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-blue-600">
              <Bot className="h-4 w-4 text-white" />
            </AvatarFallback>
          </Avatar>
        </motion.div>
      )}

      <div
        className={`max-w-xs lg:max-w-2xl ${isUser ? "order-2" : "order-1"}`}
      >
        <motion.div
          variants={bubbleVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={handleClick}
          className={`relative px-4 py-3 rounded-2xl shadow-lg backdrop-blur-sm ${
            isUser
              ? isError
                ? "bg-gradient-to-br from-red-600 to-red-700 text-white"
                : "bg-gradient-to-br from-blue-600 to-blue-700 text-white"
              : "bg-white/10 text-slate-100 border border-white/20"
          } ${isError ? "ring-2 ring-red-400/50 cursor-pointer" : ""}`}
        >
          {/* Message Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium opacity-70">
                {isUser ? "You" : "AI Assistant"}
              </span>
              <span className="text-xs opacity-50">
                {message.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>

            <div className="flex items-center space-x-1">
              {/* Voice playback button for AI messages */}
              {!isUser && canPlayAudio && onPlayAudio && onStopAudio && (
                <Tooltip
                  content={isPlaying ? "Stop audio" : "Play message aloud"}
                >
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleAudioClick}
                    className={`p-1.5 rounded-full transition-all duration-200 ${
                      isPlaying
                        ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 shadow-lg shadow-red-500/20"
                        : "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 shadow-lg shadow-blue-500/20"
                    } opacity-70 hover:opacity-100`}
                  >
                    {isPlaying ? (
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 0.8, repeat: Infinity }}
                      >
                        <Pause className="w-3 h-3" />
                      </motion.div>
                    ) : (
                      <Volume2 className="w-3 h-3" />
                    )}
                  </motion.button>
                </Tooltip>
              )}

              <Tooltip content={`Message ${message.status}`}>
                <StatusIcon status={message.status} />
              </Tooltip>
            </div>
          </div>

          {/* Message Content */}
          <div className="space-y-2">
            {isUser ? (
              // Simple formatting for user messages
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                {message.text}
              </p>
            ) : (
              // Rich formatting for AI messages
              <div className="prose prose-invert max-w-none">
                {formatAIResponse(message.text)}
              </div>
            )}

            {/* Playing audio indicator */}
            {isPlaying && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center space-x-2 pt-2 border-t border-blue-400/30"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="w-2 h-2 bg-blue-400 rounded-full"
                />
                <span className="text-xs text-blue-400 font-medium">
                  Playing audio...
                </span>
                <motion.div className="flex space-x-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ opacity: [0.4, 1, 0.4] }}
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                      className="w-1 h-3 bg-blue-400 rounded-full"
                    />
                  ))}
                </motion.div>
              </motion.div>
            )}

            {/* Audio attachment indicator */}
            {message.audioUrl && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="flex items-center space-x-2 pt-2 border-t border-white/20"
              >
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-xs opacity-70">
                  Audio response available
                </span>
              </motion.div>
            )}

            {/* Error message */}
            {isError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="text-xs text-red-200 pt-1 border-t border-red-400/30"
              >
                Failed to send message. Click to retry.
              </motion.div>
            )}
          </div>

          {/* Message tail */}
          <div
            className={`absolute ${
              isUser ? "right-0 translate-x-1/2" : "left-0 -translate-x-1/2"
            } bottom-3 w-0 h-0 ${
              isUser
                ? isError
                  ? "border-l-8 border-l-red-600 border-t-4 border-b-4 border-t-transparent border-b-transparent"
                  : "border-l-8 border-l-blue-600 border-t-4 border-b-4 border-t-transparent border-b-transparent"
                : "border-r-8 border-r-white/10 border-t-4 border-b-4 border-t-transparent border-b-transparent"
            }`}
          />
        </motion.div>
      </div>

      {/* Avatar for user messages */}
      {isUser && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: index * 0.1 + 0.2 }}
          className="ml-3 mt-1 flex-shrink-0"
        >
          <Avatar className="w-8 h-8">
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600">
              <User className="h-4 w-4 text-white" />
            </AvatarFallback>
          </Avatar>
        </motion.div>
      )}
    </motion.div>
  );
};

export default MessageBubble;
